import {useMemo, useCallback} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useSnackbar} from "notistack";
import {useApiQuery} from "../api/hooks";
import apiClient from "../api/apiClient";
import {usePreloaderStore} from "../stores";

/**
 * Централизованный хук для управления состоянием страницы LegendsEdit.
 * Содержит данные о точках и методы для работы с игрой и точками.
 */
export default function useLegendsEditState(legendsId) {
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();
    const {show: showPreloader, hide: hidePreloader} = usePreloaderStore();

    // ============ Query Keys ============
    const queryKeys = {
        points: ['legends', 'points', legendsId],
        rewardConfig: ['legends', 'reward-config', legendsId],
    };

    // ============ Queries ============
    const {data: pointsData, isLoading: pointsLoading} = useApiQuery(
        queryKeys.points,
        `/api/legends/admin/legends/find-by-smena?legendsId=${legendsId}`,
        {refetchOnMount: 'always', enabled: false} // Данные берём из родителя
    );

    // ============ Computed Data ============
    const points = useMemo(() => {
        if (!pointsData?.pointSet) return [];
        return [...pointsData.pointSet].sort((a, b) => a.id - b.id);
    }, [pointsData]);

    // ============ Invalidation ============
    const invalidatePoints = () => {
        queryClient.invalidateQueries({queryKey: ['legends', 'games']});
    };

    // ============ Game Actions ============
    const updateStatus = async (status) => {
        showPreloader();
        try {
            await apiClient.put(
                `/api/legends/admin/legends/update-status?id=${legendsId}&status=${status}`
            );
            queryClient.invalidateQueries({queryKey: ['legends', 'games']});
            enqueueSnackbar("Статус обновлён", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при обновлении статуса", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const saveGame = async (gameData) => {
        showPreloader();
        try {
            await apiClient.put(
                `/api/legends/admin/legends/save?id=${legendsId}`,
                gameData
            );
            enqueueSnackbar("Сохранено", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при сохранении", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const downloadQr = async () => {
        showPreloader();
        try {
            const response = await apiClient.get(
                `/api/legends/admin/legends/download-qr?id=${legendsId}`,
                {responseType: 'blob'}
            );
            const blob = new Blob([response.data], {type: 'application/zip'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `legends_qr_${Date.now()}.zip`;
            link.click();
            window.URL.revokeObjectURL(url);
            enqueueSnackbar("Скачивание началось", {variant: "success"});
        } catch (error) {
            // Тело ошибки приходит как Blob (responseType: 'blob') — достаём реальное сообщение бэка.
            let message = error.message;
            try {
                const data = error.response?.data;
                const text = data instanceof Blob ? await data.text() : null;
                const parsed = text ? JSON.parse(text) : data;
                message = parsed?.message || parsed?.error || message;
            } catch { /* оставляем дефолтное сообщение */ }
            enqueueSnackbar(message, {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    // ============ Reward Config Actions ============
    const getRewardConfig = async () => {
        try {
            const response = await apiClient.get(
                `/api/legends/admin/reward-configs/find-by-legends?legendsId=${legendsId}`
            );
            return response.data;
        } catch (error) {
            return null;
        }
    };

    const saveRewardConfig = async (configData) => {
        showPreloader();
        try {
            const response = await apiClient.put(
                `/api/legends/admin/reward-configs/save?legendsId=${legendsId}`,
                configData
            );
            enqueueSnackbar("Настройки наград сохранены", {variant: "success"});
            return response.data;
        } catch (error) {
            enqueueSnackbar("Ошибка при сохранении настроек наград", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    // ============ Student States Actions ============
    const getParticipants = useCallback(async () => {
        try {
            const response = await apiClient.get(
                `/api/legends/admin/states/list-by-legends?legendsId=${legendsId}`
            );
            return response.data;
        } catch (error) {
            return [];
        }
    }, [legendsId]);

    const resetParticipantProgress = async (stateId) => {
        showPreloader();
        try {
            await apiClient.put(`/api/legends/admin/states/reset?id=${stateId}`);
            enqueueSnackbar("Прогресс сброшен", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при сбросе прогресса", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const moveParticipantToPosition = async (stateId, position) => {
        showPreloader();
        try {
            await apiClient.put(`/api/legends/admin/states/move-to?id=${stateId}&position=${position}`);
            enqueueSnackbar("Участник перемещён", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || "Ошибка при перемещении", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const getParticipantSequences = async (stateId) => {
        try {
            const response = await apiClient.get(
                `/api/legends/admin/sequences/list-by-state?stateId=${stateId}`
            );
            return response.data;
        } catch (error) {
            return [];
        }
    };

    const deleteParticipant = async (stateId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/legends/admin/states/delete?id=${stateId}`);
            enqueueSnackbar("Участник удалён", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении участника", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    // ============ Points Actions ============
    const createPoint = async () => {
        showPreloader();
        try {
            const response = await apiClient.post(
                `/api/legends/admin/points/create?legendsId=${legendsId}`
            );
            invalidatePoints();
            enqueueSnackbar("Точка создана", {variant: "success"});
            return response.data;
        } catch (error) {
            enqueueSnackbar("Ошибка при создании точки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const savePoint = async (pointId, pointData, newFiles = []) => {
        showPreloader();
        try {
            const formData = new FormData();

            const mediaList = [
                ...(pointData.mediaList || []).map((m, idx) => ({id: m.mediaFileId || m.id, orderId: idx})),
                ...newFiles.map((_, idx) => ({id: null, orderId: (pointData.mediaList?.length || 0) + idx}))
            ];

            const jsonBlob = new Blob(
                [JSON.stringify({
                    name: pointData.name,
                    code: pointData.code,
                    question: pointData.question,
                    mediaList
                })],
                { type: 'application/json' }
            );
            formData.append('request', jsonBlob);

            newFiles.forEach(file => {
                formData.append('newFiles', file);
            });

            const response = await apiClient.put(
                `/api/legends/admin/points/save?id=${pointId}`,
                formData,
                {headers: {'Content-Type': 'multipart/form-data'}}
            );
            invalidatePoints();
            enqueueSnackbar("Сохранено", {variant: "success"});
            return response.data;
        } catch (error) {
            enqueueSnackbar("Ошибка при сохранении точки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deletePoint = async (pointId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/legends/admin/points/delete?id=${pointId}`);
            invalidatePoints();
            enqueueSnackbar("Точка удалена", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении точки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const loadPointDetails = async (pointId) => {
        // Для Legends детали точки уже есть в pointSet, но можно добавить отдельный endpoint
        // Пока возвращаем null - данные берутся из локального state
        return null;
    };

    return {
        // Data
        points,
        isLoading: pointsLoading,

        // Game actions
        updateStatus,
        saveGame,
        downloadQr,

        // Reward config actions
        getRewardConfig,
        saveRewardConfig,

        // Participants actions
        getParticipants,
        getParticipantSequences,
        resetParticipantProgress,
        moveParticipantToPosition,
        deleteParticipant,

        // Points actions
        createPoint,
        savePoint,
        deletePoint,
        loadPointDetails,

        // Invalidation
        invalidatePoints,
    };
}
